import Image from 'next/image';
import { useState } from 'react';

import { EPICBrief } from '@/components/atomic/atoms';
import {
  Text,
  TextKeyValuePair,
  TextWithIcon
} from '@/components/atomic/nuclei';
import { mediaStoreBaseURL, staticMediaStoreBaseURL } from '@/config/common';
import { useIsMobile } from '@/helpers/screenResolutionHelper';
import { parseNumberedLabel } from '@/lib/utils';
import {
  getLabelForEventRequestIsEndToEnd,
  getSidebarBodyData
} from '@/services/myEvents.service';

const EventCartListSideBarHead = ({
  displayUserEventProfile,
  isMobile,
  userEvent
}) => {
  const {
    userEventDetails: [{ checkoutEvent = {} } = {}] = [],
    userEventNumber,
    zendeskTicketDetail
  } = userEvent;
  const { eventRequestIsEndToEnd } = zendeskTicketDetail || {};
  return (
    <div className='flex flex-col md:gap-4 gap-1'>
      <div className='flex items-center gap-4'>
        <div className={`rounded-lg ${isMobile ? 'w-auto' : ''}`}>
          <Image
            alt='Event'
            className='rounded-lg'
            height={isMobile ? 64 : 100}
            id='user-event-icon'
            priority={true}
            src={`${mediaStoreBaseURL}/${checkoutEvent.iconUrl}`}
            style={{
              height: isMobile ? 64 : 100,
              width: isMobile ? 64 : 100
            }}
            unoptimized={true}
            width={isMobile ? 64 : 100}
          />
        </div>
        <TextKeyValuePair
          {...{
            className: 'flex-col md:!gap-2 !gap-1',
            label: checkoutEvent?.event.name,
            labelClass:
              'text-brand md:text-xl text-base md:w-72 w-64 truncate-text font-semibold',
            spaceTop: 'mt-0',
            value: getLabelForEventRequestIsEndToEnd(eventRequestIsEndToEnd),
            valueClassName: 'md:text-base text-xs font-medium text-nero'
          }}
        />
      </div>
      <div className='flex flex-col'>
        <div className='flex justify-between mb-2 mt-1 gap-2 sm:gap-4'>
          <div className='self-center'>
            <TextWithIcon
              {...{
                className:
                  'flex border border-brand md:rounded-lg rounded px-2 py-1 items-center gap-2 cursor-pointer sm:gap-4',
                icon: 'view.svg',
                onClick: displayUserEventProfile,
                label: `${isMobile ? 'View' : 'Event'}`,
                labelStyle:
                  'text-brand text-sm font-medium cursor-pointer self-center leading-4'
              }}
            />
          </div>
          <div className='flex flex-wrap gap-2 sm:gap-4'>
            <TextWithIcon
              {...{
                className:
                  'bg-white border-neutral border rounded-lg px-3 py-1 self-center flex',
                icon: 'event-profile-icon.svg',
                iconHeight: 20,
                iconWidth: 20,
                id: 'event-number',
                label: parseNumberedLabel(userEventNumber),
                labelStyle: 'text-sm font-medium text-dim-gray'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const PlannerInfo = ({ isMobile, opsUser }) => (
  <div className='flex gap-4 py-1'>
    <Image
      alt='event planner brand icon'
      className='self-center'
      height={isMobile ? 36 : 48}
      src={`${staticMediaStoreBaseURL}/icons/event-planner-brand-icon.svg`}
      width={isMobile ? 36 : 48}
    />
    <Text
      {...{
        className:
          'text-base md:text-lg font-semibold text-dim-gray self-center',
        content: opsUser?.name || 'NA'
      }}
    />
  </div>
);

const EventCartListSideBarBody = ({ router, setShowToast, userEvent }) => {
  const [isMobile] = useIsMobile();

  const {
    user: { id: userId },
    userEventDetails,
    zendeskTicketId
  } = userEvent;
  const [
    {
      eventAddress,
      eventContactName,
      eventDate,
      eventStartTime,
      eventVerticals,
      expectedGuestCount,
      opsUser
    }
  ] = userEventDetails;

  const sidebarBodyData = getSidebarBodyData({
    eventAddress,
    eventContactName,
    eventDate,
    eventStartTime,
    eventVerticals,
    expectedGuestCount,
    isMobile,
    router,
    userId,
    zendeskTicketId
  });

  return (
    <>
      <PlannerInfo {...{ isMobile, opsUser }} />
      {sidebarBodyData.map((props, index) => (
        <TextWithIcon
          key={`${props.id}-${index}`}
          {...props}
        />
      ))}
      <div className='md:mt-2 md:mb-4 border-t border-gray'></div>
      <EPICBrief
        {...{
          setShowToast,
          userEvent
        }}
      />
    </>
  );
};

const UserEventCartDetailsSideBarOrganism = ({
  displayUserEventProfile,
  isMobile,
  setShowToast,
  userEvent
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const showEventCartListSideBarBody = isMobile ? showInfo : true;

  return (
    <div
      className={`relative md:w-108 md:px-6 bg-white py-4 md:pl-10 md:pr-10 pt-5 flex flex-col gap-3 ${
        isMobile ? 'border-b-2 border-brand rounded-b-2xl pb-4 px-5' : ''
      }`}
    >
      <div className={`md:pt-5 flex flex-col md:gap-4 gap-3`}>
        <EventCartListSideBarHead
          {...{
            displayUserEventProfile,
            isMobile,
            userEvent
          }}
        />
        {showEventCartListSideBarBody && (
          <EventCartListSideBarBody
            {...{
              isMobile,
              setShowToast,
              userEvent
            }}
          />
        )}
      </div>
      {isMobile && (
        <div
          className='absolute right-4 bottom-0 transform translate-y-1/2 rounded-full p-1 z-10 cursor-pointer -m-1'
          onClick={() => setShowInfo(!showInfo)}
        >
          <Image
            {...{
              alt: 'show info',
              className: `transform ${showInfo ? 'rotate-180' : ''}`,
              height: 40,
              src: `${staticMediaStoreBaseURL}/icons/down-arrow-gradient-v1.svg`,
              width: 40
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserEventCartDetailsSideBarOrganism;
